import SampleMonitor2 from "./SampleMonitor2";
import { useState, useEffect } from "react";
import HistoryData from "./HistoryData";

function Stats() {
  // State management
  const [viewMode, setViewMode] = useState(null); // 'history', 'live', or null
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch vehicles data on component mount
  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        const response = await fetch(
          "https://icloud.assetscontrols.com:3443/OpenApi/Admin",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              FTokenID: "3585535a-137c-499e-82ad-af9feff7d8e7",
              FAction: "QueryAdminAssetList",
            }),
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.Result === 200) {
          setVehicles(
            data.FObject.map((vehicle) => ({
              name: vehicle.FVehicleName || vehicle.FAssetID,
              fguid: vehicle.FGUID,
              assetId: vehicle.FAssetID,
            }))
          );
        } else {
          throw new Error(data.Message || "Failed to fetch vehicles");
        }
      } catch (err) {
        setError(err.message);
        console.error("Error fetching vehicles:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchVehicles();
  }, []);

  // Handle vehicle selection
  const handleVehicleChange = (e) => {
    const selectedFguid = e.target.value;
    if (!selectedFguid) {
      setSelectedVehicle(null);
      return;
    }

    const vehicle = vehicles.find((v) => v.fguid === selectedFguid);
    setSelectedVehicle(vehicle);
  };

  // View mode handlers
  const showHistoryView = () => setViewMode("history");
  const showLiveView = () => setViewMode("live");
  const closeViews = () => setViewMode(null);

  return (
    <div className="min-h-screen bg-gray-100 p-4 md:p-8 w-full">
      <div className="bg-white rounded-lg shadow-md p-4 md:p-6 w-full px-4 md:px-8 lg:px-12">
        {/* Vehicle selection and view controls */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <select
            onChange={handleVehicleChange}
            value={selectedVehicle?.fguid || ""}
            className="border border-gray-300 rounded-md px-4 py-2 flex-grow md:flex-grow-0 md:w-64"
            disabled={loading || error}
          >
            <option value="">-- Select Vehicle --</option>
            {loading && <option value="">Loading vehicles...</option>}
            {error && <option value="">Error loading vehicles</option>}
            {vehicles.map((vehicle) => (
              <option key={vehicle.fguid} value={vehicle.fguid}>
                {vehicle.name} ({vehicle.assetId})
              </option>
            ))}
          </select>

          <div className="flex flex-wrap justify-center gap-2">
            <button
              onClick={showHistoryView}
              className={` moButtons px-4 py-2 rounded-md font-medium transition-colors ${
                viewMode === "history"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
              disabled={!selectedVehicle}
            >
              History Data
            </button>
            <button
              onClick={showLiveView}
              className={`moButtons px-4 py-2 rounded-md font-medium transition-colors ${
                viewMode === "live"
                  ? "bg-green-600 text-white"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
              disabled={!selectedVehicle}
            >
              Live Data
            </button>
            <button
              onClick={closeViews}
              className="moButtons px-4 py-2 rounded-md bg-gray-200 text-gray-700 font-medium hover:bg-gray-300 transition-colors"
              disabled={!viewMode}
            >
              Close All
            </button>
          </div>
        </div>

        {/* Main content area */}
        <div className="border-t pt-4">
          {viewMode === "history" && (
            <div className="animate-fadeIn">
              <h2 className="text-xl font-semibold mb-4 text-blue-700">
                Historical Data - {selectedVehicle?.name}
              </h2>
              <HistoryData
                fguid={selectedVehicle?.fguid}
                vehicleName={selectedVehicle?.name}
              />
            </div>
          )}

          {viewMode === "live" && (
            <div className="animate-fadeIn">
              <h2 className="text-xl font-semibold mb-4 text-green-700">
                Live Data Stream - {selectedVehicle?.name}
              </h2>
              <SampleMonitor2
                fguid={selectedVehicle?.fguid}
                vehicleName={selectedVehicle?.name}
              />
            </div>
          )}

          {!viewMode && (
            <div className="text-center py-8 md:py-12">
              <div className="text-gray-500 text-lg mb-4">
                {loading
                  ? "Loading vehicle data..."
                  : error
                  ? "Error loading vehicles"
                  : "No data view selected"}
              </div>
              <p className="text-gray-400">
                {!loading &&
                  !error &&
                  "Select a vehicle and choose a view mode to begin"}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Stats;
